import { useState, useRef, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Camera,
  Image as ImageIcon,
  Plus,
  Trash,
  PencilSimple,
  Eye,
  EyeSlash,
  Upload,
  X,
  SpinnerGap,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Lazy loaded image component with thumbnail -> full resolution
export function LazyImage({ thumbnailUrl, fullUrl, alt, className, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  const handleLoad = () => setLoaded(true);
  const handleError = () => setHasError(true);

  const handleClick = () => {
    if (onClick) onClick();
    else setShowFull(true);
  };

  // Build proper URL - handle both absolute and relative URLs
  const buildUrl = (url) => {
    if (!url) return '';
    // If already absolute URL, use as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise prepend backend URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Ensure no double slashes
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${cleanUrl}`;
  };

  const thumbnailSrc = buildUrl(thumbnailUrl);
  const fullSrc = buildUrl(fullUrl);

  return (
    <>
      <div className={`relative overflow-hidden ${className}`} onClick={handleClick}>
        {!loaded && !hasError && (
          <div className="absolute inset-0 bg-zinc-200 animate-pulse flex items-center justify-center">
            <ImageIcon size={24} className="text-zinc-400" />
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 bg-zinc-100 flex items-center justify-center">
            <ImageIcon size={24} className="text-zinc-300" />
          </div>
        )}
        <img
          ref={imgRef}
          src={thumbnailSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover cursor-pointer transition-opacity ${loaded && !hasError ? 'opacity-100' : 'opacity-0'}`}
        />
      </div>

      {/* Full resolution lightbox modal */}
      <Dialog open={showFull} onOpenChange={setShowFull}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-black/95">
          <button
            onClick={() => setShowFull(false)}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          <div className="flex items-center justify-center min-h-[300px] max-h-[80vh]">
            <img
              src={fullSrc}
              alt={alt}
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Photo upload component with camera support
export function PhotoUpload({ visitId, onUploadComplete, disabled, fullWidth }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [modalOpen, setModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview({ file, dataUrl: reader.result });
        setModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!preview?.file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", preview.file);
      formData.append("caption", caption);
      formData.append("visibility", visibility);

      const response = await fetch(`${API}/photos/visit/${visitId}`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Photo uploaded successfully");
        onUploadComplete?.(data);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload photo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setPreview(null);
    setCaption("");
    setVisibility("internal");
    setModalOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      <Button
        variant="outline"
        size={fullWidth ? "default" : "sm"}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || uploading}
        className={fullWidth 
          ? "w-full border-2 border-dashed h-12" 
          : "border-2"
        }
      >
        <Camera size={18} className="mr-2" />
        Add Photo
      </Button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">Upload Photo</DialogTitle>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 p-1">
              <div className="relative rounded-sm overflow-hidden border-2 border-zinc-200">
                <img src={preview.dataUrl} alt="Preview" className="w-full h-48 object-cover" />
                <button
                  onClick={resetForm}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white"
                >
                  <X size={16} />
                </button>
              </div>

              <div>
                <Label>Caption (optional)</Label>
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a description..."
                  className="mt-1 border-2"
                />
              </div>

              <div>
                <Label>Visibility</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger className="mt-1 border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <EyeSlash size={16} />
                        Internal Only
                      </div>
                    </SelectItem>
                    <SelectItem value="customer">
                      <div className="flex items-center gap-2">
                        <Eye size={16} />
                        Visible to Customer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={resetForm} className="flex-1 border-2">
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                >
                  {uploading ? (
                    <>
                      <SpinnerGap size={18} className="mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} className="mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Note input component
export function NoteInput({ visitId, onNoteAdded, disabled, fullWidth }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!noteText.trim()) return;

    setSaving(true);
    try {
      const response = await fetch(`${API}/notes/visit/${visitId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note_text: noteText, visibility }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Note added");
        onNoteAdded?.(data);
        setNoteText("");
        setVisibility("internal");
        setModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add note");
      }
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size={fullWidth ? "default" : "sm"}
        onClick={() => setModalOpen(true)}
        disabled={disabled}
        className={fullWidth 
          ? "w-full border-2 border-dashed h-12" 
          : "border-2"
        }
      >
        <Plus size={18} className="mr-2" />
        Add Note
      </Button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">Add Note</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 p-1">
            <div>
              <Label>Note</Label>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter your note..."
                className="mt-1 border-2 min-h-[120px]"
              />
            </div>

            <div>
              <Label>Visibility</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger className="mt-1 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">
                    <div className="flex items-center gap-2">
                      <EyeSlash size={16} />
                      Internal Only
                    </div>
                  </SelectItem>
                  <SelectItem value="customer">
                    <div className="flex items-center gap-2">
                      <Eye size={16} />
                      Visible to Customer
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="flex-1 border-2">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !noteText.trim()}
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                {saving ? "Saving..." : "Save Note"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Photo gallery component with lazy loading
export function PhotoGallery({ photos, onDelete, onUpdate, canEdit }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCaption, setEditCaption] = useState("");
  const [editVisibility, setEditVisibility] = useState("internal");

  const handleEditClick = (photo) => {
    setSelectedPhoto(photo);
    setEditCaption(photo.caption || "");
    setEditVisibility(photo.visibility);
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedPhoto) return;

    try {
      const formData = new FormData();
      formData.append("caption", editCaption);
      formData.append("visibility", editVisibility);

      const response = await fetch(`${API}/photos/${selectedPhoto.photo_id}`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        toast.success("Photo updated");
        onUpdate?.();
        setEditModalOpen(false);
      }
    } catch (error) {
      toast.error("Failed to update photo");
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;

    try {
      const response = await fetch(`${API}/photos/${photoId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Photo deleted");
        onDelete?.(photoId);
      }
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  if (!photos?.length) {
    return (
      <div className="text-center py-8 text-zinc-400">
        <ImageIcon size={32} className="mx-auto mb-2" />
        <p>No photos yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <div key={photo.photo_id} className="relative group">
            <LazyImage
              thumbnailUrl={photo.thumbnail_url}
              fullUrl={photo.file_url}
              alt={photo.caption || "Photo"}
              className="aspect-square rounded-sm border border-zinc-200"
            />

            {/* Visibility badge */}
            <div className="absolute top-1 left-1">
              <Badge
                variant={photo.visibility === "customer" ? "default" : "secondary"}
                className={`text-xs ${photo.visibility === "customer" ? "bg-emerald-500" : "bg-zinc-500"}`}
              >
                {photo.visibility === "customer" ? <Eye size={10} /> : <EyeSlash size={10} />}
              </Badge>
            </div>

            {/* Actions overlay */}
            {canEdit && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => handleEditClick(photo)}
                  className="p-2 bg-white rounded-full text-zinc-700 hover:bg-zinc-100"
                >
                  <PencilSimple size={16} />
                </button>
                <button
                  onClick={() => handleDelete(photo.photo_id)}
                  className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
                >
                  <Trash size={16} />
                </button>
              </div>
            )}

            {/* Caption */}
            {photo.caption && (
              <p className="mt-1 text-xs text-zinc-500 truncate">{photo.caption}</p>
            )}
          </div>
        ))}
      </div>

      {/* Edit modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">Edit Photo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 p-1">
            {/* Thumbnail preview */}
            {selectedPhoto && (
              <div className="relative rounded-sm overflow-hidden border-2 border-zinc-200 max-h-48">
                <LazyImage
                  thumbnailUrl={selectedPhoto.thumbnail_url}
                  fullUrl={selectedPhoto.file_url}
                  alt={selectedPhoto.caption || "Photo"}
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
            
            <div>
              <Label>Caption</Label>
              <Input
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                className="mt-1 border-2"
              />
            </div>

            <div>
              <Label>Visibility</Label>
              <Select value={editVisibility} onValueChange={setEditVisibility}>
                <SelectTrigger className="mt-1 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Only</SelectItem>
                  <SelectItem value="customer">Visible to Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1 border-2">
                Cancel
              </Button>
              <Button onClick={handleUpdate} className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Notes list component
export function NotesList({ notes, onDelete, onUpdate, canEdit }) {
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState("");
  const [editVisibility, setEditVisibility] = useState("internal");

  const handleEditClick = (note) => {
    setEditingNote(note);
    setEditText(note.note_text);
    setEditVisibility(note.visibility);
  };

  const handleUpdate = async () => {
    if (!editingNote) return;

    try {
      const response = await fetch(`${API}/notes/${editingNote.note_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note_text: editText, visibility: editVisibility }),
      });

      if (response.ok) {
        toast.success("Note updated");
        onUpdate?.();
        setEditingNote(null);
      }
    } catch (error) {
      toast.error("Failed to update note");
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;

    try {
      const response = await fetch(`${API}/notes/${noteId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Note deleted");
        onDelete?.(noteId);
      }
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  if (!notes?.length) {
    return (
      <div className="text-center py-8 text-zinc-400">
        <p>No notes yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => (
        <div
          key={note.note_id}
          className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm"
        >
          {editingNote?.note_id === note.note_id ? (
            <div className="space-y-3">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="border-2 min-h-[80px]"
              />
              <Select value={editVisibility} onValueChange={setEditVisibility}>
                <SelectTrigger className="border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Only</SelectItem>
                  <SelectItem value="customer">Visible to Customer</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditingNote(null)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleUpdate} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
                </div>
                <Badge
                  variant={note.visibility === "customer" ? "default" : "secondary"}
                  className={`ml-2 text-xs ${note.visibility === "customer" ? "bg-emerald-500" : "bg-zinc-500"}`}
                >
                  {note.visibility === "customer" ? "Customer" : "Internal"}
                </Badge>
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
                <span>{note.author_name} • {format(new Date(note.created_at), "MMM d, h:mm a")}</span>
                {canEdit && (
                  <div className="flex gap-2">
                    <button onClick={() => handleEditClick(note)} className="hover:text-zinc-700">
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => handleDelete(note.note_id)} className="hover:text-red-600">
                      <Trash size={14} />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default { LazyImage, PhotoUpload, NoteInput, PhotoGallery, NotesList };
